import React, { useEffect } from 'react';
import { FaInfo } from 'react-icons/fa';
import cn from 'classnames';

import {
  useVerifyTicketCommand,
  useCreateTicketActivityCommand
} from 'legacy/events/hooks';
import { VerifyTicketResultDto, EventForUser } from 'legacy/events/types';

import Loader from 'legacy/common/components/layout/Loader';
import Button from 'components/Button';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { currencyFormat } from 'legacy/common/utils/currency';
import { fromNow } from 'legacy/common/utils/datetime';
import { useTranslation } from 'react-i18next';
import { success, error as showError } from 'legacy/common/utils/toast';

type VerificationPopUpProps = {
  eventId: string;
  qrCodeString: string;
  event?: EventForUser;
  onClose: () => void;
  onError: () => void;
  onSuccess: (data: VerifyTicketResultDto) => void;
};

const VerificationPopUp: React.FC<VerificationPopUpProps> = ({
  eventId,
  qrCodeString,
  event,
  onClose,
  onError,
  onSuccess
}) => {
  const { t } = useTranslation(['events', 'common']);
  const verifyTicketCommand = useVerifyTicketCommand();
  const createTicketActivityCommand = useCreateTicketActivityCommand();

  useEffect(() => {
    verifyTicketCommand.mutate(
      {
        eventId,
        qrCodeString
      },
      {
        onSuccess,
        onError
      }
    );
  }, []);

  const { data, error } = verifyTicketCommand;

  const handleDeny = async () => {
    if (data?.ticketId) {
      try {
        await createTicketActivityCommand.mutateAsync({
          eventId,
          ticketId: data.ticketId,
          activityType: 'denied'
        });
        success(t('events:scanQR.guestDenied'));
        onClose();
      } catch (error) {
        showError(t('events:scanQR.failedToDenyGuest'));
      }
    }
  };

  const handleAdmit = async () => {
    if (data?.ticketId) {
      try {
        await createTicketActivityCommand.mutateAsync({
          eventId,
          ticketId: data.ticketId,
          activityType: 'admitted'
        });
        success(t('events:scanQR.guestAdmitted'));
        onClose();
      } catch (error) {
        showError(t('events:scanQR.failedToAdmitGuest'));
      }
    }
  };

  return (
    <FadeIn>
      <div
        className="absolute left-0 bottom-0 w-full max-h-full bg-white rounded-t-3xl p-8 overflow-auto"
        data-testid="popup-qr-verification"
      >
        <Loader isLoading={verifyTicketCommand.isLoading}>
          <>
            {data && (
              <>
                <h1 className="flex items-center text-xl text-gray-500">
                  <div
                    className={cn(
                      'mr-2 w-10 h-10 rounded-xl flex items-center justify-center',
                      { 'bg-yellow-200': data.hasAlreadyScanned },
                      { 'bg-green-200': !data.hasAlreadyScanned }
                    )}
                  >
                    <div
                      className={cn(
                        'rounded-full border-1 border-orange-700 p-1',
                        { 'border-orange-700': data.hasAlreadyScanned },
                        { 'border-green-700': !data.hasAlreadyScanned }
                      )}
                    >
                      <FaInfo
                        size="10"
                        className={
                          data.hasAlreadyScanned
                            ? 'text-orange-700'
                            : 'text-green-700'
                        }
                      />
                    </div>
                  </div>
                  <span>
                    {data.hasAlreadyScanned
                      ? t('events:scanQR.thisTicketHasAlreadyBeenScanned')
                      : t('events:scanQR.qrCodeVerified')}
                  </span>
                </h1>

                {(data.transaction?.tier.imageUrl || data.tier?.imageUrl) && (
                  <div className="mt-6 flex justify-center">
                    <img
                      src={
                        data.transaction?.tier.imageUrl || data.tier?.imageUrl
                      }
                      alt="Ticket Tier"
                      className="w-full max-w-xs rounded-lg"
                    />
                  </div>
                )}

                <div className="mt-6">
                  <div className="grid grid-cols-2 text-lg font-normal">
                    {data.transaction && (
                      <>
                        <div>
                          {t('events:scanQR.name', { postProcess: 'colon' })}
                        </div>
                        <div className="font-semibold">
                          {data.transaction.user.name}
                        </div>

                        <div>
                          {t('events:scanQR.amountPaid', {
                            postProcess: 'colon'
                          })}
                        </div>
                        <div className="font-semibold">
                          {`${currencyFormat(data.transaction.amount)} ${
                            data.transaction.currency
                          }`}
                        </div>
                      </>
                    )}

                    <div>
                      {t('events:scanQR.tier', { postProcess: 'colon' })}
                    </div>
                    <div className="font-semibold">
                      {data.transaction?.tier.name || data.name}
                    </div>

                    {data.scanDate && (
                      <>
                        <div>
                          {t('events:scanQR.lastScanned', {
                            postProcess: 'colon'
                          })}
                        </div>
                        <div className="font-semibold">
                          {fromNow(data.scanDate)}
                        </div>
                      </>
                    )}
                  </div>

                  {data?.tierRequestAnswers?.length > 0 && (
                    <div className="mt-10">
                      <p className="text-lg font-semibold">
                        {t('events:scanQR.screeningQuestions')}
                      </p>
                      {data?.tierRequestAnswers?.map((answer) => (
                        <div key={answer.questionId} className="mt-4">
                          <p className="text-base font-semibold">
                            {answer.question}
                          </p>
                          <p className="text-base font-normal">
                            {answer.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {data?.answers?.length > 0 && (
                    <div className="mt-10">
                      <p className="text-lg font-semibold">
                        {t('events:scanQR.guestQuestions')}
                      </p>
                      {data?.answers?.map((answer) => (
                        <div key={answer.questionId} className="mt-4">
                          <p className="text-base font-semibold">
                            {answer.question}
                          </p>
                          <p className="text-base font-normal">
                            {answer.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="w-full my-10">
                  {event?.enableEntryApprovalControls ? (
                    <div className="flex gap-4">
                      <Button
                        onClick={handleDeny}
                        disabled={createTicketActivityCommand.isLoading}
                        className="flex-1"
                        variant="secondary"
                        size="large"
                      >
                        {t('common:buttons.deny')}
                      </Button>
                      <Button
                        onClick={handleAdmit}
                        disabled={createTicketActivityCommand.isLoading}
                        className="flex-1"
                        variant="primaryDark"
                        size="large"
                      >
                        {t('common:buttons.admit')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => {
                        onClose();
                      }}
                      variant="primaryDark"
                      size="large"
                    >
                      {t('common:buttons.ok')}
                    </Button>
                  )}
                </div>
              </>
            )}

            {error && (
              <>
                <h1 className="flex items-center text-xl text-gray-500">
                  <div className="mr-2 w-10 h-10 bg-red-200 rounded-xl flex items-center justify-center">
                    <div className="rounded-full border-1 border-red-700 p-1">
                      <FaInfo size="10" className="text-red-700" />
                    </div>
                  </div>
                  <span>{t('events:scanQR.qrCodeVerificationError')}</span>
                </h1>

                <div className="mt-10">
                  <div className="text-xl text-red-500">{error.message}</div>
                  <div className="absolute bottom-0 w-full mb-10">
                    <Button
                      onClick={() => {
                        onClose();
                      }}
                      variant="primaryDark"
                    >
                      {t('common:buttons.ok')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        </Loader>
      </div>
    </FadeIn>
  );
};

export default VerificationPopUp;
